// Line Entry — the grid's "handle exceptions only" tools (canvas boards 3
// and 4): which lines need attention, the All / Needs attention / per-status
// filter, the selection band and the "Set <column> for N lines" dialog.
// Every value they write goes through the grid's own cell write, so each line
// runs its own item_change — exactly as if it had been typed.

import { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { Z_RECORD_DIALOG } from '../../layers'
import type { ColumnDef } from '../types'
import { columnFormKey } from '../utils'
import { evaluateLineLimits, type LineLimitVerdict, type ResolvedLineLimit } from './model'
import { T, btn, btnPri, overlay } from './styles'

export interface AttentionRow {
  _error?: string
  _fieldErrors?: Record<string, string>
  _limits?: Record<string, ResolvedLineLimit>
  _deleted?: boolean
  [k: string]: unknown
}

export interface LineAttention extends LineLimitVerdict {
  /** Errors, a limit warning, or a status off its bound. */
  needs: boolean
}

/** What a line needs: the engine's last verdict (`_error`/`_fieldErrors`)
 *  plus its limits re-read against the current values. */
export function lineAttention(row: AttentionRow, labelOf: (k: string) => string): LineAttention {
  const v = evaluateLineLimits(row, row._limits, labelOf)
  const errors = { ...(row._fieldErrors || {}), ...v.errors }
  const needs = !!row._error || Object.keys(errors).length > 0 || Object.keys(v.warnings).length > 0
    || (v.status != null && v.status.index !== 1)
  return { ...v, errors, needs }
}

export type GridFilter = 'all' | 'attention' | `status:${string}`

export interface FilterCounts {
  all: number
  attention: number
  byStatus: Array<{ label: string; index: number; count: number }>
}

export function filterCounts(attn: LineAttention[]): FilterCounts {
  const by = new Map<string, { label: string; index: number; count: number }>()
  let attention = 0
  for (const a of attn) {
    if (a.needs) attention += 1
    if (a.status && a.status.index !== 1 && a.status.label) {
      const e = by.get(a.status.label) || { label: a.status.label, index: a.status.index, count: 0 }
      e.count += 1
      by.set(a.status.label, e)
    }
  }
  return { all: attn.length, attention, byStatus: Array.from(by.values()) }
}

export function passesFilter(f: GridFilter, a: LineAttention): boolean {
  if (f === 'all') return true
  if (f === 'attention') return a.needs
  return a.status?.label === f.slice('status:'.length)
}

const seg: React.CSSProperties = {
  height: 30, padding: '0 11px', border: 0, borderRight: `1px solid ${T.field}`, fontSize: 13,
  fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontFamily: 'inherit',
}

export function GridFilterBar({ value, counts, onChange }: {
  value: GridFilter; counts: FilterCounts; onChange: (f: GridFilter) => void
}) {
  const items: Array<[GridFilter, string, number]> = [
    ['all', 'All', counts.all],
    ['attention', 'Needs attention', counts.attention],
    ...counts.byStatus.map(s => [`status:${s.label}` as GridFilter, s.label, s.count] as [GridFilter, string, number]),
  ]
  return (
    <div role="group" aria-label="Show lines" style={{ display: 'inline-flex', border: `1px solid ${T.field}`,
      borderRadius: 6, overflow: 'hidden' }}>
      {items.map(([f, label, n], i) => {
        const on = value === f
        return (
          <button key={f} type="button" aria-pressed={on} onClick={() => onChange(f)}
            style={{ ...seg, borderRight: i === items.length - 1 ? 0 : seg.borderRight,
              background: on ? T.accent : T.bg, color: on ? '#fff' : T.text }}>
            {label}
            <span style={{ fontFamily: T.mono, fontSize: 13, borderRadius: 8, padding: '0 6px',
              background: on ? 'rgba(255,255,255,.2)' : '#f0f3f7', color: on ? '#fff' : T.muted }}>{n}</span>
          </button>
        )
      })}
    </div>
  )
}

/** The blue band shown while lines are selected (board 4). */
export function SelectionBand({ count, actions, onClear }: {
  count: number
  actions: Array<{ label: string; run: () => void; danger?: boolean }>
  onClear: () => void
}) {
  const b: React.CSSProperties = {
    ...btn, height: 30, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,.6)', fontSize: 13,
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: T.accent,
      color: '#fff', flexWrap: 'wrap', fontSize: 14 }}>
      <b>{count.toLocaleString()} {count === 1 ? 'line' : 'lines'} selected</b>
      <span style={{ flex: 1 }} />
      {actions.map(a => <button key={a.label} type="button" onClick={a.run} style={b}>{a.label}</button>)}
      <button type="button" onClick={onClear} style={b}>Clear selection</button>
    </div>
  )
}

/** "Set <column> for N lines" (board 4). `renderInput` is the grid's own cell
 *  editor for that column, so a lookup column opens its lookup. */
export function SetForSelectedDialog({ col, count, blankCount, renderInput, onCancel, onApply }: {
  col: ColumnDef
  count: number
  blankCount: number
  renderInput: (value: unknown, onChange: (v: unknown) => void) => React.ReactNode
  onCancel: () => void
  onApply: (value: unknown, onlyBlank: boolean) => void
}) {
  const [value, setValue] = useState<unknown>('')
  const [onlyBlank, setOnlyBlank] = useState(false)
  const label = String(col.heading || col.name)
  const n = onlyBlank ? blankCount : count
  return (
    <div role="presentation" onClick={onCancel} style={{ ...overlay, zIndex: Z_RECORD_DIALOG, alignItems: 'center' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="le-set-title" onClick={e => e.stopPropagation()}
        style={{ width: 'min(400px, 100%)', background: T.bg, color: T.text, border: `1px solid ${T.border}`,
          borderRadius: 10, boxShadow: '0 16px 40px rgba(20,28,40,.22)', padding: 18, display: 'flex',
          flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h3 id="le-set-title" style={{ margin: 0, fontSize: 17, fontWeight: 600, flex: 1 }}>
            Set {label.toLowerCase()} for {count.toLocaleString()} {count === 1 ? 'line' : 'lines'}
          </h3>
          <button type="button" onClick={onCancel} aria-label="Close" style={{ ...btn, height: 28, padding: '0 8px' }}><X size={14} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: T.muted, fontWeight: 500 }}>
          {label}
          {renderInput(value, setValue)}
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          <legend style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 6 }}>Which cells</legend>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="radio" name="le-set-which" checked={!onlyBlank} onChange={() => setOnlyBlank(false)} />
            All {count.toLocaleString()} selected lines
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="radio" name="le-set-which" checked={onlyBlank} onChange={() => setOnlyBlank(true)} />
            Only the {blankCount.toLocaleString()} that are blank
          </label>
        </fieldset>
        <p style={{ margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.5 }}>
          Each line runs its own {label} rules, exactly as if you had typed it.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={btn}>Cancel</button>
          <button type="button" disabled={n === 0} onClick={() => onApply(value, onlyBlank)}
            style={{ ...btnPri, opacity: n === 0 ? 0.55 : 1 }}>
            Apply to {n.toLocaleString()} {n === 1 ? 'line' : 'lines'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** The columns any of a form's line sources lock — shaded with a lock icon
 *  in the grid header. */
export function useLockedColumns(
  sources: Array<{ lock?: string[] }> | undefined, cols: ColumnDef[],
): Set<string> {
  return useMemo(() => {
    const names = new Set<string>()
    for (const s of sources || []) for (const l of s.lock || []) names.add(String(l).toLowerCase())
    const out = new Set<string>()
    for (const c of cols) {
      if (names.has(String(c.name || '').toLowerCase()) || names.has(columnFormKey(c))) out.add(columnFormKey(c))
    }
    return out
  }, [sources, cols])
}
